import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UserPlus, Send, Utensils, ShoppingBag, Clock, MapPin, Bell, X, ExternalLink, Sparkles, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import PublicHeader from "../components/PublicHeader";

export default function Guide() {
  const [bot, setBot] = useState(null);

  useEffect(() => {
    api.get("/telegram/info").then((r) => setBot(r.data)).catch(() => setBot({ running: false }));
  }, []);

  return (
    <div className="min-h-screen bg-background bg-grain">
      <PublicHeader ctaTo="/register" ctaLabel="Registrati ora" />

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-10 sm:py-16 animate-fade-in">
        {/* Hero */}
        <section className="mb-10 sm:mb-14">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-3 h-3" /> Indietro
          </Link>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5 text-accent" /> Guida rapida
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] mb-4">
            Come si usa Schiscetta in 5 minuti.
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Una guida concreta, senza fronzoli: come ti registri, come prenoti dal sito, come prenoti dal bot, e come ritiri.
          </p>
        </section>

        {/* TOC */}
        <nav className="bg-card border border-border rounded-lg p-4 sm:p-5 mb-10 text-sm" data-testid="guide-toc">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">In questa guida</div>
          <ol className="space-y-1">
            <li>1. <a href="#registrati" className="text-primary hover:underline">Crea il tuo account</a></li>
            <li>2. <a href="#collega-telegram" className="text-primary hover:underline">Collega Telegram (opzionale ma consigliato)</a></li>
            <li>3. <a href="#prenota-web" className="text-primary hover:underline">Prenota dal sito</a></li>
            <li>4. <a href="#prenota-bot" className="text-primary hover:underline">Prenota dal bot</a></li>
            <li>5. <a href="#ritiro" className="text-primary hover:underline">Ritira il pranzo</a></li>
            <li>6. <a href="#annulla" className="text-primary hover:underline">Annullare o modificare</a></li>
          </ol>
        </nav>

        {/* Step 1 */}
        <Section id="registrati" step={1} icon={UserPlus} title="Crea il tuo account">
          <p>
            Vai su <Link to="/register" className="text-primary hover:underline">Registrati</Link>.
            Inserisci nome, email aziendale e password (almeno 6 caratteri).
            Se il tuo ufficio è già attivo su Schiscetta, lo trovi nel menu a tendina;
            altrimenti chiedi al tuo responsabile di compilare il <Link to="/contatti" className="text-primary hover:underline">form contatti</Link> per attivare la tua sede.
          </p>
          <Tip>
            Usa l'email aziendale (es. <code>mario.rossi@giustizia.it</code>): aiuta i tuoi colleghi a riconoscerti nelle prenotazioni.
          </Tip>
        </Section>

        {/* Step 2 */}
        <Section id="collega-telegram" step={2} icon={Send} title="Collega Telegram (consigliato)">
          <p>
            Dopo aver creato l'account, vai su <strong>"Telegram"</strong> in alto.
            Tocca <strong>"Apri il bot"</strong> — il tuo account si collega automaticamente
            in un tap, senza copiare codici.
          </p>
          <p>
            Da quel momento riceverai notifiche utili sul telefono:
          </p>
          <ul className="list-disc list-outside ml-5 space-y-1 text-muted-foreground">
            <li>nuovi piatti pubblicati dai colleghi che cucinano,</li>
            <li>promemoria prima del cutoff (orario limite per prenotare),</li>
            <li>conferme delle tue prenotazioni con indirizzo di ritiro.</li>
          </ul>
          <Tip>
            Da qualsiasi schermata del bot, tocca i pulsanti grandi sotto la tastiera
            (<strong>📋 Menu</strong>, <strong>🛒 Ordina</strong>, <strong>📦 Mie prenotazioni</strong>).
            Sono i comandi più usati, sempre a portata.
          </Tip>
        </Section>

        {/* Step 3 */}
        <Section id="prenota-web" step={3} icon={ShoppingBag} title="Prenota dal sito">
          <p>
            Vai su <strong>Menu</strong> dal menu principale.
            Vedi tutti i piatti prenotabili oggi: nome, foto, prezzo, porzioni disponibili,
            orario entro cui devi prenotare, punto di ritiro.
          </p>
          <p>Per prenotare:</p>
          <ol className="list-decimal list-outside ml-5 space-y-1 text-muted-foreground">
            <li>Tocca <strong>"Prenota"</strong> sul piatto che ti interessa.</li>
            <li>Scegli quante porzioni (massimo 5 per prenotazione).</li>
            <li>Spunta <strong>"Ti servono le posate?"</strong> se ti servono forchetta/coltello/tovagliolo.</li>
            <li>Tocca <strong>"Conferma"</strong>. La prenotazione è registrata e il preparatore riceve subito una notifica.</li>
          </ol>
        </Section>

        {/* Step 4 */}
        <Section id="prenota-bot" step={4} icon={Send} title="Prenota dal bot Telegram">
          <p>
            Se hai collegato Telegram, puoi prenotare anche da lì. È più veloce se sei
            in riunione o lontano dalla scrivania.
          </p>
          <div className="bg-muted/40 rounded-lg p-4 my-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              Tre comandi che ti bastano
            </div>
            <ul className="space-y-1.5 text-sm">
              <li><code className="px-1.5 py-0.5 bg-white border border-border rounded text-primary">/menu</code> — vedi i piatti di oggi</li>
              <li><code className="px-1.5 py-0.5 bg-white border border-border rounded text-primary">/ordina</code> — prenota: scegli piatto → porzioni → posate (sì/no)</li>
              <li><code className="px-1.5 py-0.5 bg-white border border-border rounded text-primary">/mieprenotazioni</code> — controlla cosa hai ordinato per oggi</li>
            </ul>
          </div>
          {bot?.running && bot?.deep_link && (
            <a
              href={bot.deep_link}
              target="_blank"
              rel="noreferrer"
              data-testid="guide-open-bot"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-medium rounded-md hover:bg-[#436343]"
            >
              Apri il bot ora <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </Section>

        {/* Step 5 */}
        <Section id="ritiro" step={5} icon={MapPin} title="Ritira il pranzo">
          <p>
            All'ora di pranzo vai al punto di ritiro indicato dal preparatore
            (es. <em>stanza 142, secondo piano</em>). Trovi il punto:
          </p>
          <ul className="list-disc list-outside ml-5 space-y-1 text-muted-foreground">
            <li>nel dialog di conferma quando prenoti,</li>
            <li>nella pagina <strong>Mie prenotazioni</strong>,</li>
            <li>nell'email/notifica Telegram di conferma.</li>
          </ul>
          <p>
            <strong className="text-foreground">Si paga in contanti</strong> direttamente
            al collega che ha cucinato, come si è sempre fatto. Schiscetta tiene traccia di quanto
            ti è costato, ma <strong>non gestisce i pagamenti</strong>.
          </p>
        </Section>

        {/* Step 6 */}
        <Section id="annulla" step={6} icon={X} title="Annullare o modificare una prenotazione">
          <p>
            Puoi cancellare una prenotazione <strong>fino all'orario di cutoff</strong> stabilito
            dal preparatore (di solito mattina presto). Vai su <strong>Mie prenotazioni</strong>
            e tocca la X sull'ordine che vuoi annullare.
          </p>
          <p>
            Dopo il cutoff non si può più disdire: il piatto è già in preparazione e il collega ha contato anche su di te.
          </p>
        </Section>

        {/* CTA finale */}
        <section className="bg-primary text-white rounded-2xl p-7 sm:p-10 text-center mt-10" data-testid="guide-cta-bottom">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-3">
            Pronto a smettere di andare al bar?
          </h2>
          <p className="text-white/85 mb-6 max-w-md mx-auto">
            Bastano 30 secondi per registrarti. Il primo pasto lo vedi già nel menu di domani.
          </p>
          <Link
            to="/register"
            data-testid="guide-cta-register-bottom"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-medium rounded-md hover:bg-white/90 active:scale-[0.98] transition-all"
          >
            Crea il tuo account <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-white/70 mt-4">
            Hai già un account? <Link to="/login" className="underline">Accedi qui</Link>
          </p>
        </section>
      </main>

      <footer className="border-t border-border py-8 mt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center text-xs text-muted-foreground">
          Schiscetta · piattaforma interna per uffici · 2026
        </div>
      </footer>
    </div>
  );
}

function Section({ id, step, icon: Icon, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-24" data-testid={`guide-step-${step}`}>
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-11 h-11 rounded-md bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            Passo {step}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
            {title}
          </h2>
        </div>
      </div>
      <div className="text-base text-foreground/80 leading-relaxed space-y-3 pl-0 sm:pl-[60px]">
        {children}
      </div>
    </section>
  );
}

function Tip({ children }) {
  return (
    <div className="bg-primary/5 border border-primary/15 rounded-md p-3 flex items-start gap-2">
      <span className="text-primary text-base shrink-0">💡</span>
      <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}
